import client from "@lib/apollo";
import { getPageComponent } from "@lib/pageBuilder";
import { PAGE_PATHS, PAGE_QUERY } from "@lib/queries/page";
import type { GetStaticPropsContext, NextPage } from "next";
import { GetPagePathsQuery, Pages_Default_Entry } from "__types__/graphql";
import { Container } from "@components/Container";
import { BGElement } from "@components/BGElement";

const CMSPage: NextPage<{ entry: Pages_Default_Entry }> = ({ entry }) => {
  if (!entry) return null;

  return (
    <Container
      title={entry.seo?.title ?? "Tradewinds"}
      className="relative overflow-x-hidden"
    >
      <BGElement />

      {entry.pageBuilder.map((comp) => {
        if (!comp) {
          return null;
        }
        const Component = getPageComponent(comp.typeHandle);
        return <Component key={comp.id} {...comp} />;
      })}
    </Container>
  );
};

export async function getStaticProps({ params }: GetStaticPropsContext) {
  const slug = params?.slug;

  if (!slug) {
    return {
      redirect: {
        destination: "/",
      },
    };
  }

  const { data } = await client.query<{ entry: Pages_Default_Entry }>({
    query: PAGE_QUERY,
    variables: {
      // Child page slug is last portion of entire next slug (e.g. `education-center/{events}`)
      slug: Array.isArray(slug) ? slug[slug.length - 1] : slug,
    },
  });

  if (!data.entry) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      entry: data?.entry ?? {},
    },
    revalidate: 10,
  };
}

/**
 * these pages are left to hook up
 */
const skipPaths = [
  "get-started",
  "opportunities",
  "cdao-vehicles",
  "news",
  "videos",
  "blog",
  "contact",
  "education-center",
  "events",
  "industry",
  "government",
  "ai-acquisition-playground",
];
export async function getStaticPaths() {
  const { data } = await client.query<GetPagePathsQuery>({
    query: PAGE_PATHS,
  });

  const paths = data.entries
    ?.filter((page) => !skipPaths.includes(page!.uri!))
    .map((page) => ({
      params: {
        slug: page?.uri?.split("/"),
      },
    }));

  return {
    paths,
    fallback: "blocking",
  };
}

export default CMSPage;
